import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

import { digestRecoveryPendingRow } from '@/services/recovery/recoveryPendingDigest';
import type { PendingAccountingState } from '@/types/syncRecovery';

export const SUPERSEDED_BY_REMOTE_ENTITY = 'superseded_by_remote_entity' as const;
export const PENDING_OPERATION_ACCOUNTED_FEATURE = 'pending_operation_accounted_v1';
export const PENDING_OPERATION_ACCOUNTED_DISABLED = 'pending_operation_accounted_disabled';

const SHA512_HEX = /^[0-9a-f]{128}$/;
const SHA512_BASE64 = /^[A-Za-z0-9+/]{85}[AQgw]==$/;
const ENTITY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ACTIVE_CONFLICT_STATUSES = new Set(['open', 'blocked']);

export interface PendingOperationSnapshotInput {
  id: string;
  operation: string;
  collection: string;
  itemId?: string;
  item_id?: string;
  data?: unknown;
  timestamp: number;
}

export interface AccountedPendingArchive {
  id: string;
  empresa_id: string;
  user_id: string | null;
  operation: string;
  collection: string;
  item_id: string;
  data: string | null;
  original_timestamp: number;
  accounting_state: PendingAccountingState;
  reason: string | null;
  receipt_id: string;
  payload_sha512: string;
  accounted_at: number;
  created_at: number;
  updated_at: number;
}

export interface SyncConflictRow {
  id: string;
  empresa_id: string;
  user_id?: string | null;
  pending_operation_id?: string | null;
  collection?: string | null;
  item_id?: string | null;
  payload?: string | null;
  payload_sha512?: string | null;
  status?: string | null;
  reason?: string | null;
  resolved_at?: number | null;
}

export interface PendingOperationLedgerRecord {
  companyId: string;
  recoveryId: string;
  receiptId: string;
  pendingOperationId: string;
  payloadSha512: string;
  accountingState: typeof SUPERSEDED_BY_REMOTE_ENTITY;
  originEntityId: string;
  successorEntityId: string;
  writeSetCount: number;
  writeSetDigest: string;
  clientBuild: number;
}

export interface PendingOperationLedgerReceipt {
  eventId: string;
  receiptId: string;
  pendingOperationId: string;
  payloadSha512: string;
  originEntityId: string;
  successorEntityId: string;
  writeSetCount: number;
  writeSetDigest: string;
}

export const TERMINAL_RESOLUTION_SCHEMA = 'app-guincho-pending-terminal-resolution/v1' as const;

export interface PendingTerminalResolution {
  schema: typeof TERMINAL_RESOLUTION_SCHEMA;
  classification: typeof SUPERSEDED_BY_REMOTE_ENTITY;
  successorEntityId: string;
  writeSetCount: number;
  writeSetSha512: string;
}

export interface PendingOperationLedger {
  accountPendingOperation(
    record: PendingOperationLedgerRecord
  ): Promise<PendingOperationLedgerReceipt>;
}

export interface SuccessorEntity {
  id: string;
  empresaId: string;
}

export interface PendingOperationTerminalizationDatabase {
  readItem<T>(collection: string, id: string): Promise<T | null>;
  createItem<T>(collection: string, item: T, skipPendingOperation?: boolean): Promise<string>;
  removePendingOperationIfUnchanged(
    id: string,
    expected: {
      operation: string;
      collection: string;
      item_id: string;
      data: string | null;
      timestamp: number;
    }
  ): Promise<boolean>;
  updateItemIfUnchanged(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    expected: Record<string, unknown>,
    skipPendingOperation?: boolean
  ): Promise<boolean>;
}

export interface AccountedTerminalConflictDatabase extends PendingOperationTerminalizationDatabase {
  listItems<T>(collection: string, query?: unknown): Promise<T[]>;
}

const serializePendingPayload = (data: unknown): string | null => {
  if (data === null || data === undefined) return null;
  return typeof data === 'string' ? data : JSON.stringify(data);
};

export const sha512Hex = (value: string): string => Array.from(nacl.hash(naclUtil.decodeUTF8(value)))
  .map(byte => byte.toString(16).padStart(2, '0'))
  .join('');

export const digestPendingOperationSnapshot = (
  operation: PendingOperationSnapshotInput
): string => digestRecoveryPendingRow(
  'pending_operations',
  operation as unknown as Record<string, unknown>
);

export const emptyWriteSetDigest = (): string => sha512Hex('[]');

export const isActiveSyncConflict = (row: { status?: unknown } | null | undefined): boolean => {
  const status = typeof row?.status === 'string' && row.status.trim()
    ? row.status.trim().toLowerCase()
    : 'open';
  return ACTIVE_CONFLICT_STATUSES.has(status);
};

export const casExpectedFromArchive = (
  archive: AccountedPendingArchive
): ReturnType<typeof asCasExpected> => {
  if (!archive.data) {
    throw new Error('Arquivo accounted sem snapshot CAS.');
  }
  const payload = JSON.parse(archive.data) as Record<string, unknown>;
  const stored = payload.cas_expected;
  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    const record = stored as Record<string, unknown>;
    return {
      operation: String(record.operation ?? ''),
      collection: String(record.collection ?? ''),
      item_id: String(record.item_id ?? ''),
      data: typeof record.data === 'string' || record.data === null ? record.data : null,
      timestamp: Number(record.timestamp),
    };
  }
  return {
    operation: archive.operation,
    collection: archive.collection,
    item_id: archive.item_id,
    data: typeof payload.payload === 'string' || payload.payload === null
      ? payload.payload
      : serializePendingPayload(payload.payload),
    timestamp: archive.original_timestamp,
  };
};

export const accountedArchiveIdFor = (
  pendingOperationId: string,
  payloadSha512: string
): string => `accounted:superseded:${pendingOperationId}:${payloadSha512.slice(0, 24)}`;

export const accountedArchiveIdForConflict = (conflictId: string): string => {
  if (!conflictId.startsWith('conflict:') || conflictId.length === 'conflict:'.length) {
    throw new Error('Identidade do conflito nao permite pareamento deterministico.');
  }
  return `accounted:${conflictId.slice('conflict:'.length)}`;
};

const requireEntityId = (value: string, label: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!ENTITY_ID.test(normalized)) {
    throw new Error(`${label} invalido.`);
  }
  return normalized;
};

const requireSha512 = (value: string, label: string): string => {
  const normalized = value.trim().toLowerCase();
  if (!SHA512_HEX.test(normalized)) {
    throw new Error(`${label} invalido.`);
  }
  return normalized;
};

const requireRecoveryPayloadSha512 = (value: string, label: string): string => {
  const normalized = value.trim();
  if (!SHA512_BASE64.test(normalized)) {
    throw new Error(`${label} invalido.`);
  }
  return normalized;
};

const RESOLUTION_KEYS = new Set([
  'schema',
  'classification',
  'successorEntityId',
  'writeSetCount',
  'writeSetSha512',
]);

export const parsePendingTerminalResolution = (value: unknown): PendingTerminalResolution => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Resolucao terminal ausente ou invalida.');
  }
  const row = value as Record<string, unknown>;
  if (Object.keys(row).some(key => !RESOLUTION_KEYS.has(key))) {
    throw new Error('Resolucao terminal com campos extras.');
  }
  if (row.schema !== TERMINAL_RESOLUTION_SCHEMA) {
    throw new Error('Schema da resolucao terminal invalido.');
  }
  if (row.classification !== SUPERSEDED_BY_REMOTE_ENTITY) {
    throw new Error('Classificacao terminal nao suportada.');
  }
  if (!Number.isInteger(row.writeSetCount) || Number(row.writeSetCount) < 0) {
    throw new Error('writeSetCount da resolucao invalido.');
  }
  return {
    schema: TERMINAL_RESOLUTION_SCHEMA,
    classification: SUPERSEDED_BY_REMOTE_ENTITY,
    successorEntityId: requireEntityId(String(row.successorEntityId ?? ''), 'Sucessor da resolucao'),
    writeSetCount: row.writeSetCount as number,
    writeSetSha512: requireSha512(String(row.writeSetSha512 ?? ''), 'Digest do write-set da resolucao'),
  };
};

const itemIdOf = (operation: PendingOperationSnapshotInput): string => (
  typeof operation.itemId === 'string' && operation.itemId
    ? operation.itemId
    : String(operation.item_id ?? '')
);

export const asCasExpected = (operation: PendingOperationSnapshotInput) => ({
  operation: operation.operation,
  collection: operation.collection,
  item_id: itemIdOf(operation),
  data: serializePendingPayload(operation.data),
  timestamp: operation.timestamp,
});

export const pendingSnapshotsMatch = (
  left: PendingOperationSnapshotInput,
  right: PendingOperationSnapshotInput
): boolean => {
  const expected = asCasExpected(left);
  const actual = asCasExpected(right);
  return expected.operation === actual.operation
    && expected.collection === actual.collection
    && expected.item_id === actual.item_id
    && expected.data === actual.data
    && expected.timestamp === actual.timestamp;
};

export const localOperationIdFor = (pendingOperationId: string): string => (
  pendingOperationId.startsWith('pending_operations:')
    ? pendingOperationId
    : `pending_operations:${pendingOperationId}`
);

const assertArchiveMatches = (
  archive: AccountedPendingArchive,
  expected: {
    companyId: string;
    userId: string;
    operation: PendingOperationSnapshotInput;
    payloadSha512: string;
    receiptId: string;
    successorEntityId: string;
    writeSetCount: number;
    writeSetDigest: string;
  }
): void => {
  const payload = archive.data ? JSON.parse(archive.data) as Record<string, unknown> : {};
  if (
    archive.empresa_id !== expected.companyId ||
    archive.user_id !== expected.userId ||
    archive.operation !== expected.operation.operation ||
    archive.collection !== expected.operation.collection ||
    archive.item_id !== itemIdOf(expected.operation) ||
    archive.accounting_state !== SUPERSEDED_BY_REMOTE_ENTITY ||
    archive.receipt_id !== expected.receiptId ||
    archive.payload_sha512 !== expected.payloadSha512 ||
    payload.successor_entity_id !== expected.successorEntityId ||
    payload.write_set_count !== expected.writeSetCount ||
    payload.write_set_digest !== expected.writeSetDigest
  ) {
    throw new Error('Arquivo local de supersessao diverge da evidencia existente.');
  }
};

export const terminalizeSupersededByRemoteEntity = async (input: {
  database: PendingOperationTerminalizationDatabase;
  ledger: PendingOperationLedger;
  lookupSuccessor(companyId: string, successorEntityId: string): Promise<SuccessorEntity | null>;
  companyId: string;
  userId: string;
  recoveryId: string;
  receiptId: string;
  clientBuild: number;
  operation: PendingOperationSnapshotInput;
  expectedPayloadSha512: string;
  successorEntityId: string;
  writeSetCount: number;
  writeSetDigest?: string;
  now?: () => number;
}): Promise<{
  status: 'accounted' | 'already_accounted';
  archiveId: string;
  receiptId: string;
  eventId: string;
}> => {
  const expectedPayloadSha512 = requireRecoveryPayloadSha512(
    input.expectedPayloadSha512,
    'Digest do payload'
  );
  const successorEntityId = requireEntityId(input.successorEntityId, 'Sucessor remoto');
  const originEntityId = requireEntityId(itemIdOf(input.operation), 'Entidade de origem');
  if (!Number.isSafeInteger(input.writeSetCount) || input.writeSetCount < 0) {
    throw new Error('Contagem do write-set invalida.');
  }
  const writeSetDigest = requireSha512(
    input.writeSetDigest ?? (input.writeSetCount === 0 ? emptyWriteSetDigest() : ''),
    'Digest do write-set'
  );

  if (digestPendingOperationSnapshot(input.operation) !== expectedPayloadSha512) {
    throw new Error('Digest informado diverge do payload original; CAS recusado.');
  }

  const current = await input.database.readItem<PendingOperationSnapshotInput>(
    'pending_operations',
    input.operation.id
  );
  const archiveId = accountedArchiveIdFor(input.operation.id, expectedPayloadSha512);
  const existingArchive = await input.database.readItem<AccountedPendingArchive>(
    'accounted_pending_operations',
    archiveId
  );

  if (current) {
    if (!pendingSnapshotsMatch(input.operation, current)) {
      throw new Error('Snapshot da pendencia divergiu; fila ativa preservada.');
    }
  } else if (!existingArchive) {
    throw new Error('Pendencia ausente e sem arquivo terminal; recusando remocao.');
  }

  const successor = await input.lookupSuccessor(input.companyId, successorEntityId);
  if (!successor) {
    throw new Error('Sucessor remoto ausente.');
  }
  if (successor.empresaId !== input.companyId || successor.id !== successorEntityId) {
    throw new Error('Sucessor remoto diverge do par esperado.');
  }

  let ledgerReceipt: PendingOperationLedgerReceipt;
  try {
    ledgerReceipt = await input.ledger.accountPendingOperation({
      companyId: input.companyId,
      recoveryId: input.recoveryId,
      receiptId: input.receiptId,
      pendingOperationId: input.operation.id,
      payloadSha512: expectedPayloadSha512,
      accountingState: SUPERSEDED_BY_REMOTE_ENTITY,
      originEntityId,
      successorEntityId,
      writeSetCount: input.writeSetCount,
      writeSetDigest,
      clientBuild: input.clientBuild,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes(PENDING_OPERATION_ACCOUNTED_DISABLED)) {
      throw new Error(PENDING_OPERATION_ACCOUNTED_DISABLED);
    }
    throw error;
  }

  if (
    ledgerReceipt.receiptId !== input.receiptId ||
    ledgerReceipt.pendingOperationId !== input.operation.id ||
    ledgerReceipt.payloadSha512 !== expectedPayloadSha512 ||
    ledgerReceipt.originEntityId !== originEntityId ||
    ledgerReceipt.successorEntityId !== successorEntityId ||
    ledgerReceipt.writeSetCount !== input.writeSetCount ||
    ledgerReceipt.writeSetDigest !== writeSetDigest
  ) {
    throw new Error('Readback do ledger remoto diverge do CAS local.');
  }

  const archiveExpected = {
    companyId: input.companyId,
    userId: input.userId,
    operation: input.operation,
    payloadSha512: expectedPayloadSha512,
    receiptId: input.receiptId,
    successorEntityId,
    writeSetCount: input.writeSetCount,
    writeSetDigest,
  };

  if (existingArchive) {
    assertArchiveMatches(existingArchive, archiveExpected);
  } else {
    const accountedAt = (input.now ?? Date.now)();
    await input.database.createItem('accounted_pending_operations', {
      id: archiveId,
      empresa_id: input.companyId,
      user_id: input.userId,
      operation: input.operation.operation,
      collection: input.operation.collection,
      item_id: originEntityId,
      data: JSON.stringify({
        payload: serializePendingPayload(input.operation.data),
        origin_entity_id: originEntityId,
        successor_entity_id: successorEntityId,
        write_set_count: input.writeSetCount,
        write_set_digest: writeSetDigest,
        event_id: ledgerReceipt.eventId,
        cas_expected: asCasExpected(input.operation),
      }),
      original_timestamp: input.operation.timestamp,
      accounting_state: SUPERSEDED_BY_REMOTE_ENTITY,
      reason: SUPERSEDED_BY_REMOTE_ENTITY,
      receipt_id: input.receiptId,
      payload_sha512: expectedPayloadSha512,
      accounted_at: accountedAt,
      created_at: accountedAt,
      updated_at: accountedAt,
    } satisfies AccountedPendingArchive, true);
  }

  if (current) {
    const removed = await input.database.removePendingOperationIfUnchanged(
      input.operation.id,
      asCasExpected(input.operation)
    );
    if (!removed) {
      throw new Error('Pendencia mudou durante o CAS; fila ativa preservada.');
    }
  }

  return {
    status: existingArchive && !current ? 'already_accounted' : 'accounted',
    archiveId,
    receiptId: ledgerReceipt.receiptId,
    eventId: ledgerReceipt.eventId,
  };
};

const archivePayloadMatchesConflict = (
  archiveData: string | null,
  conflictPayload: string | null
): boolean => {
  if (archiveData === conflictPayload) return true;
  if (archiveData === null) return false;
  try {
    const parsed = JSON.parse(archiveData) as Record<string, unknown>;
    if (!Object.prototype.hasOwnProperty.call(parsed, 'payload')) return false;
    const nestedPayload = parsed.payload;
    const serialized = typeof nestedPayload === 'string'
      ? nestedPayload
      : nestedPayload === null
        ? null
        : JSON.stringify(nestedPayload);
    return serialized === conflictPayload;
  } catch {
    return false;
  }
};

const validateAccountedTerminalConflictEvidence = async (input: {
  database: PendingOperationTerminalizationDatabase;
  conflictId: string;
  accountedArchiveId: string;
  expectedPayloadSha512: string;
  companyId: string;
  userId: string;
}): Promise<SyncConflictRow> => {
  const expectedPayloadSha512 = requireSha512(input.expectedPayloadSha512, 'Digest do conflito');
  const conflict = await input.database.readItem<SyncConflictRow>('sync_conflicts', input.conflictId);
  if (!conflict) {
    throw new Error('Conflito terminal ausente; evidencias historicas nao podem ser inventadas.');
  }
  if (conflict.empresa_id !== input.companyId) {
    throw new Error('Conflito fora do tenant; encerramento recusado.');
  }
  if (conflict.user_id !== input.userId) {
    throw new Error('Conflito fora do usuario; encerramento recusado.');
  }
  if (
    typeof conflict.pending_operation_id !== 'string' || !conflict.pending_operation_id ||
    typeof conflict.collection !== 'string' || !conflict.collection ||
    typeof conflict.item_id !== 'string' || !conflict.item_id ||
    typeof conflict.reason !== 'string' || !conflict.reason ||
    !Object.prototype.hasOwnProperty.call(conflict, 'payload') ||
    (typeof conflict.payload !== 'string' && conflict.payload !== null) ||
    typeof conflict.status !== 'string'
  ) {
    throw new Error('Conflito terminal sem identidade ou payload integral.');
  }
  if (conflict.payload_sha512 !== expectedPayloadSha512) {
    throw new Error('Digest do conflito diverge; CAS recusado.');
  }
  const conflictIdentity = conflict.id.split(':');
  if (
    !conflictIdentity.includes(conflict.pending_operation_id) ||
    conflictIdentity.at(-1) !== expectedPayloadSha512.slice(0, 24)
  ) {
    throw new Error('Identidade do conflito diverge da pendencia ou do digest.');
  }

  const deterministicArchiveId = accountedArchiveIdForConflict(conflict.id);
  if (input.accountedArchiveId !== deterministicArchiveId) {
    throw new Error('Arquivo accounted nao corresponde ao par deterministico do conflito.');
  }

  const archive = await input.database.readItem<AccountedPendingArchive>(
    'accounted_pending_operations',
    input.accountedArchiveId
  );
  if (!archive) {
    throw new Error('Arquivo accounted ausente; conflito ativo preservado.');
  }
  if (
    archive.id !== deterministicArchiveId ||
    archive.empresa_id !== input.companyId ||
    archive.user_id !== input.userId ||
    archive.accounting_state !== 'conflict_materialized' ||
    archive.reason !== conflict.reason ||
    typeof archive.operation !== 'string' || archive.operation.length === 0 ||
    archive.collection !== conflict.collection ||
    archive.item_id !== conflict.item_id ||
    archive.payload_sha512 !== expectedPayloadSha512 ||
    !archivePayloadMatchesConflict(archive.data, conflict.payload)
  ) {
    throw new Error('Arquivo accounted diverge do conflito; nada foi apagado.');
  }

  return conflict;
};

export const resolveAccountedTerminalConflict = async (input: {
  database: PendingOperationTerminalizationDatabase;
  conflictId: string;
  accountedArchiveId: string;
  expectedPayloadSha512: string;
  companyId: string;
  userId: string;
  now?: () => number;
}): Promise<{ status: 'resolved' | 'already_resolved' }> => {
  const expectedPayloadSha512 = requireSha512(input.expectedPayloadSha512, 'Digest do conflito');
  const conflict = await validateAccountedTerminalConflictEvidence({
    ...input,
    expectedPayloadSha512,
  });

  if (!isActiveSyncConflict(conflict)) {
    if (conflict.status === 'resolved') return { status: 'already_resolved' };
    throw new Error('Estado de conflito inesperado; evidencias preservadas.');
  }

  const resolvedAt = (input.now ?? Date.now)();
  const updated = await input.database.updateItemIfUnchanged(
    'sync_conflicts',
    input.conflictId,
    {
      status: 'resolved',
      resolved_at: resolvedAt,
      updated_at: resolvedAt,
    },
    {
      empresa_id: input.companyId,
      user_id: input.userId,
      pending_operation_id: conflict.pending_operation_id,
      collection: conflict.collection,
      item_id: conflict.item_id,
      reason: conflict.reason,
      payload: conflict.payload,
      payload_sha512: expectedPayloadSha512,
      status: conflict.status ?? 'open',
    },
    true
  );
  if (!updated) {
    throw new Error('Conflito mudou durante o CAS; colecao ativa preservada.');
  }
  return { status: 'resolved' };
};

export const resolveAllAccountedTerminalConflicts = async (input: {
  database: AccountedTerminalConflictDatabase;
  companyId: string;
  userId: string;
  now?: () => number;
}): Promise<{ resolved: number; alreadyResolved: number }> => {
  const listed = await input.database.listItems<SyncConflictRow>('sync_conflicts', {
    empresa_id: input.companyId,
  });
  const active = listed
    .filter(row => (
      row.empresa_id === input.companyId &&
      isActiveSyncConflict(row)
    ))
    .sort((left, right) => left.id.localeCompare(right.id));

  if (active.some(row => row.user_id !== input.userId)) {
    throw new Error('Tenant possui conflito ativo fora do usuario atual; recovery recusado.');
  }

  const prepared = [] as Array<{
    conflictId: string;
    accountedArchiveId: string;
    expectedPayloadSha512: string;
  }>;

  // Todo o conjunto e validado antes do primeiro CAS para evitar encerramento parcial
  // causado por evidencia incompleta ou divergente ja presente no aparelho.
  for (const conflict of active) {
    const digest = requireSha512(String(conflict.payload_sha512 ?? ''), 'Digest do conflito');
    const archiveId = accountedArchiveIdForConflict(conflict.id);
    await preflightAccountedTerminalConflict({
      database: input.database,
      conflictId: conflict.id,
      accountedArchiveId: archiveId,
      expectedPayloadSha512: digest,
      companyId: input.companyId,
      userId: input.userId,
    });
    prepared.push({
      conflictId: conflict.id,
      accountedArchiveId: archiveId,
      expectedPayloadSha512: digest,
    });
  }

  let resolved = 0;
  let alreadyResolved = 0;
  for (const pair of prepared) {
    const result = await resolveAccountedTerminalConflict({
      database: input.database,
      ...pair,
      companyId: input.companyId,
      userId: input.userId,
      now: input.now,
    });
    if (result.status === 'resolved') resolved += 1;
    else alreadyResolved += 1;
  }
  return { resolved, alreadyResolved };
};

const preflightAccountedTerminalConflict = async (input: {
  database: PendingOperationTerminalizationDatabase;
  conflictId: string;
  accountedArchiveId: string;
  expectedPayloadSha512: string;
  companyId: string;
  userId: string;
}): Promise<void> => {
  const conflict = await input.database.readItem<SyncConflictRow>('sync_conflicts', input.conflictId);
  if (!conflict || !isActiveSyncConflict(conflict)) {
    throw new Error('Conflito deixou de estar ativo durante o preflight.');
  }
  await validateAccountedTerminalConflictEvidence(input);
};
